"use client";

import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { Filter, Plus } from "lucide-react";
import {
  arrow,
  autoUpdate,
  flip,
  FloatingArrow,
  FloatingPortal,
  offset,
  shift,
  size,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from "@floating-ui/react";
import { Button } from "@/components";
import { cn } from "@/lib/utils/cn";
import type { FilterConfig } from "@/lib/types/data.types";
import type { DataFilterProps } from "./types";
import { getDefaultFilterColumn, getOperatorsForColumn } from "./helpers";
import { FilterRow } from "./FilterRow";

export function DataFilter({
  columns,
  filters,
  onFiltersChange,
  className,
  triggerLabel = "Filters",
  defaultColumnKey,
  renderTrigger,
}: DataFilterProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [localFilters, setLocalFilters] = useState<FilterConfig[]>(filters);
  const [floatingMaxHeight, setFloatingMaxHeight] = useState<number>();
  const arrowRef = useRef(null);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    whileElementsMounted: autoUpdate,
    placement: "bottom-start",
    middleware: [
      offset(12),
      flip(),
      shift({ padding: 8 }),
      size({
        padding: 8,
        apply({ availableHeight }) {
          setFloatingMaxHeight(Math.max(1, availableHeight));
        },
      }),
      arrow({ element: arrowRef }),
    ],
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: "dialog" });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
    role,
  ]);

  const sortedColumns = useMemo(function sortColumnsByLabel() {
    return [...columns].sort((a, b) => a.label.localeCompare(b.label));
  }, [columns]);

  // Sync local state when external filters change
  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  // Auto-add a pre-filled row when opening with no filters
  useEffect(() => {
    if (isOpen && localFilters.length === 0 && sortedColumns.length > 0) {
      const firstCol = getDefaultFilterColumn(sortedColumns, defaultColumnKey);
      if (!firstCol) return;
      const ops = getOperatorsForColumn(firstCol);
      setLocalFilters([
        { column: firstCol.key, operator: ops[0]?.value ?? "", value: "" },
      ]);
    }
    // Only trigger when the panel opens, not on localFilters changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, sortedColumns, defaultColumnKey]);

  const addEmptyRow = useCallback(() => {
    if (sortedColumns.length === 0) return;
    const firstCol = getDefaultFilterColumn(sortedColumns, defaultColumnKey);
    if (!firstCol) return;
    const ops = getOperatorsForColumn(firstCol);
    setLocalFilters((prev) => [
      ...prev,
      { column: firstCol.key, operator: ops[0]?.value ?? "", value: "" },
    ]);
  }, [sortedColumns, defaultColumnKey]);

  const updateFilter = useCallback((index: number, updates: Partial<FilterConfig>) => {
    setLocalFilters((prev) =>
      prev.map((f, i) => (i === index ? { ...f, ...updates } : f))
    );
  }, []);

  const removeFilter = useCallback((index: number) => {
    const removed = localFilters[index];
    const next = localFilters.filter((_, i) => i !== index);
    setLocalFilters(next);
    if (next.length === 0) {
      if (removed?.value) onFiltersChange([]);
      setIsOpen(false);
    }
  }, [localFilters, onFiltersChange]);

  const applyFilters = useCallback(() => {
    const withValues = localFilters.filter((f) => f.value);
    setLocalFilters(withValues);
    onFiltersChange(withValues);
    setIsOpen(false);
  }, [localFilters, onFiltersChange]);

  const clearAll = useCallback(() => {
    setLocalFilters([]);
    onFiltersChange([]);
    setIsOpen(false);
  }, [onFiltersChange]);

  const activeCount = filters.filter((f) => f.value).length;

  return (
    <div className={cn("relative inline-block", className)}>
      {/* Trigger */}
      {renderTrigger ? (
        <div ref={refs.setReference} {...getReferenceProps()}>
          {renderTrigger({
            filterCount: activeCount,
            onClick: () => setIsOpen(!isOpen),
          })}
        </div>
      ) : (
        <Button
          ref={refs.setReference}
          variant="white"
          size="sm"
          icon={Filter}
          {...getReferenceProps()}
        >
          {triggerLabel}
          {activeCount > 0 && (
            <span className="ml-0.5 px-1.5 py-0.5 text-xs font-semibold rounded-full bg-navigator-primary text-white">
              {activeCount}
            </span>
          )}
        </Button>
      )}

      {/* Floating dropdown */}
      {isOpen && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={{
              ...floatingStyles,
              maxHeight: floatingMaxHeight,
              width: "min(520px, calc(100vw - 16px))",
            }}
            className="z-50 bg-white rounded-xl shadow-lg border border-gray-200 p-4 overflow-auto"
            {...getFloatingProps()}
          >
            <FloatingArrow
              ref={arrowRef}
              context={context}
              fill="white"
              strokeWidth={1}
              stroke="#e5e7eb"
              width={12}
              height={6}
            />

            {/* Header */}
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-semibold text-gray-700">
                Filters
              </span>
              {localFilters.length > 0 && (
                <Button variant="ghost" size="md" onClick={clearAll}>
                  Clear all
                </Button>
              )}
            </div>

            {/* Filter rows */}
            <div className="flex flex-col gap-2">
              {localFilters.map((filter, index) => (
                <FilterRow
                  key={index}
                  columns={sortedColumns}
                  filter={filter}
                  onChange={(updates) => updateFilter(index, updates)}
                  onRemove={() => removeFilter(index)}
                />
              ))}
            </div>

            {/* Footer */}
            <div className="mt-3 flex items-center justify-end gap-2">
              <Button
                variant="ghost"
                size="md"
                onClick={addEmptyRow}
                icon={Plus}
              >
                Add filter
              </Button>
              <Button variant="primary" size="md" onClick={applyFilters}>
                Apply
              </Button>
            </div>
          </div>
        </FloatingPortal>
      )}
    </div>
  );
}
